/* eslint-disable max-lines-per-function */
import {
   MedicalServiceAlreadyExistsFault,
   MedicalServiceCreateCommand,
   MedicalServiceNameForReceiptParameters,
   PractitionerMedicalServiceParameters,
   PractitionerSettlementParameters,
} from '@dreryk/egabinet-private-medical-service-frontend-api';
import { Money } from '@dreryk/money-common-api';

import { useCqrsCommand } from '@dreryk/command-query-react-common';
import { dispatchSnackbarPositiveEvent } from '@dreryk/react-components';
import { Choice } from '@dreryk/watermelon';

import { medicalServiceTranslationKeys } from '../../translations';
import { nameForInvoicingVisitor } from '../shared/nameForInvoicing/nameForInvocing.utils';
import { createMedicalServiceSettlementTypeVisitor } from '../shared/settlement/settlement.utils';

import { MedicalServiceCreateFormBean } from './MedicalServiceCreateForm';

type MedicalServiceCreateActionRenderProps = {
   createMedicalService: (formData: MedicalServiceCreateFormBean) => Promise<void>;
};

type MedicalServiceCreateActionProps = {
   children: (renderProps: MedicalServiceCreateActionRenderProps) => JSX.Element;
   onSuccess: VoidFunction;
   onMedicalServiceAlreadyExistsFault: VoidFunction;
};

export const MedicalServiceCreateAction = (props: MedicalServiceCreateActionProps): JSX.Element => {
   const { executeAsync } = useCqrsCommand<void, MedicalServiceCreateFormBean, MedicalServiceAlreadyExistsFault>(
      (parameters) => {
         const command = new MedicalServiceCreateCommand();
         const medicalServiceSettlementTypeVisitor = createMedicalServiceSettlementTypeVisitor(
            parameters.settlementType
         );

         command.name = parameters.name!;
         nameForInvoicingVisitor(parameters)({
            withNameForInvoicing: ({ nameForInvoicing, usingNameAsNameForInvoicing }) => {
               command.usingNameAsNameForInvoicing = usingNameAsNameForInvoicing;
               if (!parameters.usingNameAsNameForInvoicing) {
                  command.nameForInvoicing = Choice.of(nameForInvoicing);
               }
            },
            withoutNameForInvoicing: () => {
               command.usingNameAsNameForInvoicing = true;
            },
         });

         if (parameters.nameForReceiptType) {
            command.nameForReceipt = Choice.of<MedicalServiceNameForReceiptParameters>({
               type: parameters.nameForReceiptType,
               value: parameters.nameForReceiptType === 'OTHER' ? Choice.of(parameters.nameForReceipt!) : undefined,
            });
         }

         command.defaultPrice = Money.of(parameters.defaultPrice!);
         command.taxRateId = parameters.taxRate.id;
         command.defaultSettlementParameters = medicalServiceSettlementTypeVisitor<PractitionerSettlementParameters>({
            visitNone: () => ({
               settlementType: 'NONE',
            }),
            visitFixed: () => ({
               settlementType: 'FIXED',
               fixedValue: Choice.of(Money.of(parameters.fixedValue!)),
            }),
            visitProportional: () => ({
               settlementType: 'PROPORTIONAL',
               proportionalValue: Choice.of(parameters.proportionalValue!),
            }),
         });
         command.practitionerMedicalServices = parameters.practitioners!.map(
            (practitioner): PractitionerMedicalServiceParameters => {
               const practitionerSettlementTypeVisitor = createMedicalServiceSettlementTypeVisitor(
                  practitioner.settlementType
               );

               if (practitioner.usingDefaultConfiguration) {
                  return {
                     practitionerId: practitioner.id,
                     usingDefaultConfiguration: true,
                     individualPrice: Choice.withUseDefault(),
                     settlementParameters: Choice.withUseDefault(),
                  };
               }
               return {
                  practitionerId: practitioner.id,
                  usingDefaultConfiguration: false,
                  individualPrice: Choice.of(Money.of(practitioner.individualPrice!)),
                  settlementParameters: Choice.of(
                     practitionerSettlementTypeVisitor<PractitionerSettlementParameters>({
                        visitNone: () => ({
                           settlementType: 'NONE',
                        }),
                        visitFixed: () => ({
                           settlementType: 'FIXED',
                           fixedValue: Choice.of(Money.of(practitioner.fixedValue!)),
                        }),
                        visitProportional: () => ({
                           settlementType: 'PROPORTIONAL',
                           proportionalValue: Choice.of(practitioner.proportionalValue!),
                        }),
                     })
                  ),
               };
            }
         );

         return command;
      },
      {
         onSuccess: () => {
            dispatchSnackbarPositiveEvent(medicalServiceTranslationKeys.create.modal.confirmationText);
            props.onSuccess();
         },
         onFault: {
            'egabinet-private-medical-service-frontend-api:MedicalServiceAlreadyExistsFault':
               props.onMedicalServiceAlreadyExistsFault,
         },
      }
   );

   return props.children({
      createMedicalService: executeAsync,
   });
};
